import { useState } from "react";
import { Upload, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatError } from "@/lib/api";

const downloadTemplate = (format) => downloadFile(`/admin/questions/template?format=${format}`, `learnai_question_template.${format}`);

export async function downloadFile(path, filename) {
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error(formatError(e)); }
}

export default function BulkUpload({ onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/questions/upload", fd);
      setResult(data);
      if (data.inserted) { toast.success(`${data.inserted} questions added as approved`); onDone(); }
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };

  return (
    <>
      <Button data-testid="aq-bulk-upload-btn" variant="outline" onClick={() => { setOpen(true); setResult(null); setFile(null); }}><Upload className="mr-1 h-4 w-4" />Bulk upload</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl bg-surface" data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle>Bulk upload questions</DialogTitle>
            <DialogDescription>Upload a CSV or Excel file. Valid rows are added straight to the approved question bank.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 text-sm">
            <div className="rounded-lg bg-canvas-2 p-4">
              <p className="mb-2 font-medium text-ink">1. Download a template</p>
              <p className="mb-3 text-xs text-ink-3">Columns: subject_id, topic, difficulty, question, option_a–option_d, correct_option (A–D), explanation. The Excel template includes a sheet listing every subject_id and topic.</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" data-testid="template-csv-btn" onClick={() => downloadTemplate("csv")}><Download className="mr-1 h-3.5 w-3.5" />CSV template</Button>
                <Button size="sm" variant="outline" data-testid="template-xlsx-btn" onClick={() => downloadTemplate("xlsx")}><Download className="mr-1 h-3.5 w-3.5" />Excel template</Button>
              </div>
            </div>
            <div>
              <p className="mb-2 font-medium text-ink">2. Upload your file (max 1000 rows, 2 MB)</p>
              <input data-testid="bulk-file-input" type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-solid file:px-4 file:py-2 file:text-white" />
            </div>
            <Button data-testid="bulk-submit-btn" disabled={!file || busy} onClick={upload}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload & import"}</Button>
            {result && (
              <div data-testid="bulk-result" className="rounded-lg border border-line p-4">
                <p><b className="text-emerald-700">{result.inserted}</b> imported · <b className="text-amber-700">{result.skipped}</b> skipped</p>
                {result.errors.length > 0 && (
                  <ul className="mt-2 max-h-48 space-y-1 overflow-auto text-xs text-red-700" data-testid="bulk-errors">
                    {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
